import { Conversation } from '@botpress/client'
import { minimalFormatDate } from '@bpinternal/shared'
import { Button, Skeleton, useIsMobile } from '@bpinternal/ui-kit-next'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { DateTime } from 'luxon'
import { PropsWithChildren, useEffect } from 'react'
import { Line, LineChart, ResponsiveContainer } from 'recharts'
import { Avatar } from '~/elements'
import { dateShortcutDetails } from '~/features/analytics'
import { useAnalyticsMetric } from '~/features/home/hooks/useAnalyticsMetric'
import { useBotDailyAiSpend } from '~/features/usage/useBotDailyAiSpend'
import { fillMissingDatesFromDateRange } from '~/features/usage/utils'
import { listConversationsInfiniteQuery } from '~/queries/conversations'
import { useSuspenseQuery } from '~/services'
import { cn, formatters } from '~/utils'
import { useAgentAnalyticsContext } from './analytics/AgentAnalyticsContext'

type BaseProps = {
  workspaceId: string
  botId: string
  className?: string
}

export const HitlQueueCardSkeleton = ({ workspaceId, botId, className }: BaseProps) => (
  <SideCard
    className={cn('flex flex-col gap-0 gap-2 rounded-lg border p-4 md:gap-6', className)}
    title="Human Handoff Queue"
    url={`/workspaces/${workspaceId}/bots/${botId}/hitl`}
    clickable
  >
    <div className="flex flex-col gap-2">
      <Skeleton className="h-6 w-8" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  </SideCard>
)

export const HitlQueueCard = ({ workspaceId, botId, className }: BaseProps) => {
  const { data: pendingConversations, refetch } = useSuspenseQuery(
    `workspaces_/$workspaceId_/bots_/$botId_/conversations/$integrationName/$tags/$participantIds/all`,
    {
      workspaceId,
      botId,
      integrationName: 'hitl',
      tags: {
        'hitl:status': 'pending',
      },
    }
  )

  useEffect(() => {
    const i = setInterval(refetch, 10000) // 10 seconds
    return () => clearInterval(i)
  }, [refetch])

  return (
    <SideCard
      className={cn('flex flex-col gap-0 gap-2 rounded-lg border p-4 md:gap-6', className)}
      title="Human Handoff Queue"
      url={`/workspaces/${workspaceId}/bots/${botId}/hitl`}
      clickable
    >
      <div className="flex flex-col gap-2">
        <span className="text-2xl font-bold">
          {pendingConversations.length === 0 ? '-' : formatters.numberLocale(pendingConversations.length, 0, 'fr-FR')}
        </span>
        <span className="text-xs text-muted-foreground">Conversations awaiting humans</span>
      </div>
    </SideCard>
  )
}

export const PerformanceCardSkeleton = ({ workspaceId, botId, className }: BaseProps) => {
  const titles = ['AI Spend', 'LLM Calls', 'LLM Errors']
  return (
    <SideCard
      className={cn('flex flex-col gap-6 rounded-lg border p-4', className)}
      title="Performance"
      url={`/workspaces/${workspaceId}/bots/${botId}/analytics`}
      clickable
    >
      {titles.map((title) => (
        <div key={title} className="flex flex-col gap-2 rounded-lg bg-neutral-50 p-4">
          <span className="text-sm font-medium text-muted-foreground">{title}</span>
          <Skeleton className="size-5" />
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
    </SideCard>
  )
}

export const PerformanceCard = ({ workspaceId, botId, className }: BaseProps) => {
  const { dateShortcut } = useAgentAnalyticsContext()
  const numberDays = dateShortcutDetails[dateShortcut ?? 'lastWeek'].days
  const endDate = new Date()
  const startDate = new Date()
  startDate.setDate(endDate.getDate() - numberDays)

  const { dailyAiSpendUsage } = useBotDailyAiSpend({
    workspaceId,
    botId,
    startDate: startDate.toISOString().split('T')[0] ?? '',
    endDate: endDate.toISOString().split('T')[0] ?? '',
  })
  const totalAiSpend = dailyAiSpendUsage.reduce((sum, record) => sum + (record.value as number), 0)
  const chartAiSpendData = fillMissingDatesFromDateRange(dailyAiSpendUsage, startDate, endDate)

  const llmErrorsData = useAnalyticsMetric(botId, workspaceId, {
    extractValue: (record) => {
      if (record && typeof record === 'object' && 'llm' in record) {
        const llm = record.llm as Record<string, unknown>
        return typeof llm?.errors === 'number' ? llm.errors : 0
      }
      return 0
    },
    dateShortcut,
    chartDataKey: 'errors',
    valueKey: 'llmErrorValues',
  })

  const { llmErrorValues, chartData: chartErrorsData } = llmErrorsData

  const llmCallsData = useAnalyticsMetric(botId, workspaceId, {
    extractValue: (record) => {
      if (record && typeof record === 'object' && 'llm' in record) {
        const llm = record.llm as Record<string, unknown>
        return typeof llm?.calls === 'number' ? llm.calls : 0
      }
      return 0
    },
    dateShortcut,
    chartDataKey: 'calls',
    valueKey: 'llmCallValues',
  })

  const { llmCallValues, chartData: chartCallsData } = llmCallsData

  return (
    <SideCard
      title="Performance"
      url={`/workspaces/${workspaceId}/bots/${botId}/analytics`}
      className={className}
      clickable
    >
      <PerformanceSubCard
        title={'AI Spend'}
        value={formatters.$nanodollars(totalAiSpend)}
        data={chartAiSpendData}
        dataKey="value"
      />
      <PerformanceSubCard
        title={'LLM Calls'}
        value={formatters.numberLocale(llmCallValues, 0, 'fr-FR')}
        data={chartCallsData}
        dataKey={'calls'}
      />
      <PerformanceSubCard
        title={'LLM Errors'}
        value={formatters.numberLocale(llmErrorValues, 0, 'fr-FR')}
        data={chartErrorsData}
        dataKey={'errors'}
      />
    </SideCard>
  )
}

type PerformanceSubCardProps = {
  title: string
  value: string
  data: Record<string, unknown>[]
  dataKey: string
}

export const PerformanceSubCard = ({ title, value, data, dataKey }: PerformanceSubCardProps) => {
  return (
    <div className="bg-card-filled flex flex-col rounded-lg p-4">
      <span className="mb-2 text-sm font-medium text-muted-foreground">{title}</span>
      <span className="text-sm">{value}</span>
      <ResponsiveContainer width="100%" height={32}>
        <LineChart data={data}>
          <Line
            type="linear"
            dataKey={dataKey}
            stroke={dataKey === 'errors' ? 'oklch(var(--color-red-500))' : 'oklch(var(--color-neutral-950))'}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

export const RecentConversationsCardSkeleton = ({ workspaceId, botId, className }: BaseProps) => {
  return (
    <SideCard
      title="Recent Conversations"
      url={`/workspaces/${workspaceId}/bots/${botId}/conversations`}
      className={cn('flex flex-col gap-6 rounded-lg border p-4', className)}
    >
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="flex w-full items-center gap-3">
          <Skeleton className="size-10 flex-none rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-5 w-full" />
          </div>
          <Skeleton className="h-4 w-8" />
        </div>
      ))}
    </SideCard>
  )
}

export const RecentConversationsCard = ({ workspaceId, botId, className }: BaseProps) => {
  const { data: fetchedConversations } = useInfiniteQuery(listConversationsInfiniteQuery({ botId, workspaceId }))
  const recentConversations = fetchedConversations?.pages.flatMap((page) => page.conversations).slice(0, 5) ?? []
  return (
    <SideCard
      title="Recent Conversations"
      url={`/workspaces/${workspaceId}/bots/${botId}/conversations`}
      className={className}
    >
      {recentConversations.length > 0 ? (
        <div className="flex flex-col gap-4">
          {recentConversations.map((conv) => (
            <ConversationRow key={conv.id} workspaceId={workspaceId} botId={botId} conversation={conv} />
          ))}
        </div>
      ) : (
        <span className="text-xs text-muted-foreground">You don't have any conversations</span>
      )}
    </SideCard>
  )
}

type ConversationRowProps = BaseProps & {
  conversation: Conversation
}

const ConversationRow = ({ workspaceId, botId, conversation }: ConversationRowProps) => {
  const {
    data: { users },
  } = useSuspenseQuery(`workspaces_/$workspaceId_/bots_/$botId_/$conversationId_/users`, {
    workspaceId,
    botId,
    conversationId: conversation.id,
  })
  const firstUser = users.at(0)

  return (
    <Link
      to={`/workspaces/$workspaceId/bots/$botId/conversations/$conversationId`}
      params={{ workspaceId, botId, conversationId: conversation.id }}
      className="rounded-lg p-2 transition-colors duration-200 ease-out hover:bg-accent hover:text-accent-foreground"
    >
      <div className="flex w-full items-center gap-3">
        {firstUser && <Avatar name={firstUser.name ?? 'Anonymous User'} pictureUrl={firstUser.pictureUrl} />}
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium">{firstUser?.name ?? 'Anonymous User'}</span>
          <span className="truncate text-sm">{conversation.tags.title ?? 'Conversation Title Unknown'}</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {minimalFormatDate(DateTime.fromISO(conversation.updatedAt).toJSDate())}
        </span>
      </div>
    </Link>
  )
}

type SideCardProps = PropsWithChildren<{
  title: string
  url: string
  clickable?: boolean
  className?: string
}>

export const SideCard = ({ title, url, clickable, children, className }: SideCardProps) => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  return (
    <div
      className={cn('flex flex-col gap-6 rounded-lg border p-4 max-lg:cursor-pointer', className)}
      onClick={() => {
        if (clickable && isMobile && url) {
          navigate({ to: url })
        }
      }}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{title}</h3>
        <Link to={url}>
          <Button variant="ghost" size="icon">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
      {children}
    </div>
  )
}
